import { Link, Stack } from '@mui/material'
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import Icon from 'components/Icon'
import { AWSAccountIdDrawer } from 'components/rbac-settings/teams/AWSAccountIdDrawer'
import {
  getLogo,
  getPresetKeyFromURL,
  isCustomUrl
} from 'components/scenarios/create/status-checks/third-party-presets'
import { useGetStatusChecksUrls } from 'components/status-checks/hooks/use-get-status-checks-urls'
import theme from 'theme'
import {
  ObservabilityToolType,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'

import type { GridCellParams } from '@mui/x-data-grid'

export const TeamIntegrationTableName: React.FC<{
  isAwsIntegrationsTable?: boolean
  hasLogoOnIntegration: boolean
  params: GridCellParams<TeamExternalIntegration>
  savedIntegrations: TeamExternalIntegration[]
}> = ({
  isAwsIntegrationsTable,
  hasLogoOnIntegration,
  params,
  savedIntegrations
}) => {
  const statusChecksUrls = useGetStatusChecksUrls()
  const integration = params.row

  return (
    <Stack
      data-testid="status-check-authentication-list-row"
      direction="row"
      spacing={2}
      sx={{ alignItems: 'center' }}
    >
      {hasLogoOnIntegration && (
        <RouterLink
          to={statusChecksUrls.editAuthenticationFromTeamExternalIntegration(
            integration
          )}
        >
          {isCustomUrl(integration.url, integration.observabilityToolType) ? (
            <Icon
              css={{
                color: 'initial',
                width: theme.space[5],
                height: theme.space[5]
              }}
              icon="wavePulse"
            />
          ) : (
            <img
              alt={getPresetKeyFromURL(integration.url)}
              css={{
                color: theme.colors.gray[1],
                width: theme.fontSizes[5],
                height: theme.fontSizes[5]
              }}
              src={getLogo(getPresetKeyFromURL(integration.url))}
            />
          )}
        </RouterLink>
      )}
      <Stack direction="column">
        {isAwsIntegrationsTable ? (
          <AWSAccountIdDrawer
            externalIntegrations={savedIntegrations}
            renderButton={({ openDrawer }) => (
              <Link onClick={openDrawer}>{integration.displayName}</Link>
            )}
            selectedAWSAccount={integration}
          />
        ) : (
          <TruncatedTextWithTooltip
            tooltipProps={{ title: integration.displayName }}
            variant="body2"
          >
            <Link
              component={RouterLink}
              data-test-status-checks-authentication-list-row-name={
                integration.displayName
              }
              to={statusChecksUrls.editAuthenticationFromTeamExternalIntegration(
                integration
              )}
            >
              {integration.displayName}
            </Link>
          </TruncatedTextWithTooltip>
        )}

        {/* Don't show the URL for AWS authentications because it is always hardcoded */}
        {integration.observabilityToolType !== ObservabilityToolType.AWS ? (
          <TruncatedTextWithTooltip
            color={theme.colors.steel[1]}
            tooltipProps={{ title: integration.url, placement: 'bottom' }}
            variant="caption"
          >
            {integration.url}
          </TruncatedTextWithTooltip>
        ) : null}
      </Stack>
    </Stack>
  )
}
